import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact credit balance chip with a "top up" CTA.
 *
 * Reads from /api/credits/me. Re-fetches on demand via the exposed
 * `refresh()` ref (used after a Coach Twin reply or a plan generation).
 */
export default function CreditBalance({ compact = false }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/credits/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCredits(res.data);
    } catch (_) { /* ignore */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Listen for the global "credits-changed" custom event others can dispatch.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('credits-changed', handler);
    return () => window.removeEventListener('credits-changed', handler);
  }, [load]);

  if (!credits) return null;

  const low = credits.balance < 25;
  return (
    <button
      onClick={() => navigate('/pricing')}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold transition-all"
      style={{
        background: low
          ? 'linear-gradient(90deg, rgba(251,191,36,0.18), rgba(251,113,133,0.18))'
          : 'linear-gradient(90deg, rgba(0,201,167,0.15), rgba(0,217,255,0.15))',
        border: `1px solid ${low ? 'rgba(251,191,36,0.4)' : 'rgba(0,201,167,0.4)'}`,
        color: low ? '#fbbf24' : '#00C9A7',
      }}
      title={`${credits.tier_label} · ${credits.monthly_credits || 0}/mo refill`}
    >
      <span>⚡</span>
      <span className="tabular-nums">{credits.balance}</span>
      {!compact && <span className="opacity-70 font-normal">credits</span>}
    </button>
  );
}

/** Helper to fire after a known credit-changing action. */
export function pingCreditBalance() {
  window.dispatchEvent(new Event('credits-changed'));
}
